import React, { useState, useEffect } from 'react';
import { getAllProgression, getWorkoutPlans } from '../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Loader2, 
  TrendingUp, 
  Star, 
  Calendar, 
  Activity,
  Dumbbell,
  Flame,
  Target,
  Award,
  ChevronUp,
  ChevronDown,
  Minus
} from 'lucide-react';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend
} from 'recharts';
import { useLanguage } from '../context/LanguageContext';

const CHART_TABS = [
  { id: 'overview', label: 'Visão Geral', icon: Activity },
  { id: 'sessions', label: 'Sessões', icon: Flame },
  { id: 'weekly', label: 'Semanal', icon: Calendar },
  { id: 'types', label: 'Tipos de Treino', icon: Dumbbell }
];

const ProgressionPage = () => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [progressions, setProgressions] = useState([]);
  const [plans, setPlans] = useState([]);
  const [timeRange, setTimeRange] = useState('30'); // days
  const [chartType, setChartType] = useState('overview');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [progressData, plansData] = await Promise.all([
          getAllProgression(),
          getWorkoutPlans()
        ]);
        
        setProgressions(progressData || []);
        setPlans(plansData || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'short'
    });
  };

  const formatDateFull = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-PT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Filter data by time range
  const filterByTimeRange = (data) => {
    const now = new Date();
    const daysAgo = new Date(now.setDate(now.getDate() - parseInt(timeRange)));
    return data.filter(item => new Date(item.created_at) >= daysAgo);
  };

  // Prepare chart data for sessions and ratings
  const sessionChartData = filterByTimeRange(progressions)
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at))
    .map((prog) => ({
      date: formatDate(prog.created_at),
      fullDate: formatDateFull(prog.created_at),
      sessoes: prog.completed_sessions || 0,
      avaliacao: prog.performance_rating || 0
    }));

  // Plans by type
  const plansByType = plans.reduce((acc, plan) => {
    const type = plan.plan_type || 'outros';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {});

  const pieChartData = Object.entries(plansByType).map(([name, value]) => ({
    name: name === 'strength' ? 'Musculacao' : 
          name === 'cardio' ? 'Cardio' : 
          name === 'hiit' ? 'HIIT' : 
          name === 'circuit' ? 'Circuito' : name,
    value
  }));

  const COLORS = ['#32CD32', '#3B82F6', '#F59E0B', '#EF4444', '#8B5CF6'];

  // Weekly activity data
  const getWeeklyData = () => {
    const weeks = {};
    filterByTimeRange(progressions).forEach(prog => {
      const date = new Date(prog.created_at);
      const weekStart = new Date(date.setDate(date.getDate() - date.getDay()));
      const weekKey = weekStart.toISOString().split('T')[0];
      
      if (!weeks[weekKey]) {
        weeks[weekKey] = { sessoes: 0, totalRating: 0, count: 0 };
      }
      weeks[weekKey].sessoes += prog.completed_sessions || 0;
      weeks[weekKey].totalRating += prog.performance_rating || 0;
      weeks[weekKey].count += 1;
    });

    return Object.entries(weeks)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-8)
      .map(([week, data]) => ({
        semana: `Sem ${new Date(week).toLocaleDateString('pt-PT', { day: '2-digit', month: 'short' })}`,
        sessoes: data.sessoes,
        mediaAvaliacao: data.count > 0 ? (data.totalRating / data.count).toFixed(1) : 0
      }));
  };

  // Calculate stats
  const filteredProgressions = filterByTimeRange(progressions);
  const totalSessions = filteredProgressions.reduce((sum, p) => sum + (p.completed_sessions || 0), 0);
  const avgRating = filteredProgressions.length > 0 
    ? (filteredProgressions.reduce((sum, p) => sum + (p.performance_rating || 0), 0) / filteredProgressions.length).toFixed(1)
    : 0;
  const totalPlans = plans.length;
  
  // Trend calculation
  const calculateTrend = () => {
    if (filteredProgressions.length < 2) return 0;
    const sorted = [...filteredProgressions].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    const half = Math.floor(sorted.length / 2);
    const firstHalf = sorted.slice(0, half);
    const secondHalf = sorted.slice(half);
    
    const avgFirst = firstHalf.reduce((sum, p) => sum + (p.completed_sessions || 0), 0) / firstHalf.length;
    const avgSecond = secondHalf.reduce((sum, p) => sum + (p.completed_sessions || 0), 0) / secondHalf.length;
    
    return ((avgSecond - avgFirst) / avgFirst * 100).toFixed(0);
  };

  const trend = calculateTrend();

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-[#1a1a1a] border border-white/20 rounded-lg p-3 shadow-xl">
          <p className="text-gray-400 text-sm mb-2">{payload[0]?.payload?.fullDate || label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="text-sm" style={{ color: entry.color }}>
              {entry.name}: <span className="font-bold">{entry.value}</span>
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-[#32CD32]" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto" data-testid="progression-page">
        {/* Header */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 
                className="text-3xl md:text-4xl font-bold tracking-tight uppercase mb-2"
                style={{ fontFamily: 'Barlow Condensed' }}
              >
                A Tua <span className="text-[#32CD32]">Progressão</span>
              </h1>
              <p className="text-[#A1A1AA]">
                Acompanha a tua evolução e desempenho ao longo do tempo
              </p>
            </div>
            
            {/* Time Range Filter */}
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-40 bg-black/50 border-white/20">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/20">
                <SelectItem value="7">Últimos 7 dias</SelectItem>
                <SelectItem value="30">Últimos 30 dias</SelectItem>
                <SelectItem value="90">Últimos 3 meses</SelectItem>
                <SelectItem value="365">Último ano</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#A1A1AA] uppercase">Sessões</p>
                  <p className="text-3xl font-bold text-white">{totalSessions}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-[#32CD32]/20 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-[#32CD32]" />
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1">
                {trend > 0 ? (
                  <ChevronUp className="w-4 h-4 text-green-500" />
                ) : trend < 0 ? (
                  <ChevronDown className="w-4 h-4 text-red-500" />
                ) : (
                  <Minus className="w-4 h-4 text-gray-500" />
                )}
                <span className={`text-xs ${trend > 0 ? 'text-green-500' : trend < 0 ? 'text-red-500' : 'text-gray-500'}`}>
                  {trend > 0 ? '+' : ''}{trend}% vs período anterior
                </span>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#121212] border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#A1A1AA] uppercase">Avaliação Média</p>
                  <p className="text-3xl font-bold text-white">{avgRating}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
                  <Star className="w-6 h-6 text-yellow-500" />
                </div>
              </div>
              <div className="mt-2 flex items-center gap-1">
                {[1, 2, 3, 4, 5].map(star => (
                  <Star 
                    key={star} 
                    className={`w-3 h-3 ${star <= avgRating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} 
                  />
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#121212] border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#A1A1AA] uppercase">Planos Criados</p>
                  <p className="text-3xl font-bold text-white">{totalPlans}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <Target className="w-6 h-6 text-blue-500" />
                </div>
              </div>
              <div className="mt-2 text-xs text-[#A1A1AA]">
                {Object.keys(plansByType).length} tipos diferentes
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#121212] border-white/10">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs text-[#A1A1AA] uppercase">Registos</p>
                  <p className="text-3xl font-bold text-white">{filteredProgressions.length}</p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-500" />
                </div>
              </div>
              <div className="mt-2 text-xs text-[#A1A1AA]">
                nos últimos {timeRange} dias
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Chart Tabs */}
        <div className="tab-scroll mb-4">
          {CHART_TABS.map(tab => {
            const TabIcon = tab.icon;
            return (
              <button
                key={tab.id}
                data-testid={`chart-tab-${tab.id}`}
                onClick={() => setChartType(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors whitespace-nowrap ${
                  chartType === tab.id
                    ? 'bg-[#32CD32]/20 text-[#32CD32] border border-[#32CD32]/30'
                    : 'bg-black/30 text-gray-400 border border-white/10 hover:border-white/30'
                }`}
              >
                <TabIcon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Charts */}
        <Card className="bg-[#121212] border-white/10 mb-8">
          <CardHeader>
            <CardTitle className="text-lg uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
              {chartType === 'overview' && 'Evolução das Sessões e Avaliações'}
              {chartType === 'sessions' && 'Sessões Completadas'}
              {chartType === 'weekly' && 'Atividade Semanal'}
              {chartType === 'types' && 'Distribuição por Tipo de Treino'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {sessionChartData.length === 0 && chartType !== 'types' ? (
              <div className="h-80 flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <Activity className="w-12 h-12 mx-auto mb-4 text-gray-600" />
                  <p>Sem dados de progressão para o período selecionado</p>
                  <p className="text-sm mt-2">Completa treinos e regista a tua progressão para ver os gráficos</p>
                </div>
              </div>
            ) : (
              <div className="h-80">
                <ResponsiveContainer width="100%" height="100%">
                  {chartType === 'overview' && (
                    <AreaChart data={sessionChartData}>
                      <defs>
                        <linearGradient id="colorSessoes" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#32CD32" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#32CD32" stopOpacity={0}/>
                        </linearGradient>
                        <linearGradient id="colorAvaliacao" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#F59E0B" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#F59E0B" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#666" tick={{ fill: '#888' }} />
                      <YAxis stroke="#666" tick={{ fill: '#888' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Area 
                        type="monotone" 
                        dataKey="sessoes" 
                        name="Sessoes"
                        stroke="#32CD32" 
                        fillOpacity={1} 
                        fill="url(#colorSessoes)" 
                      />
                      <Area 
                        type="monotone" 
                        dataKey="avaliacao" 
                        name="Avaliacao"
                        stroke="#F59E0B" 
                        fillOpacity={1} 
                        fill="url(#colorAvaliacao)" 
                      />
                    </AreaChart>
                  )}

                  {chartType === 'sessions' && (
                    <BarChart data={sessionChartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="date" stroke="#666" tick={{ fill: '#888' }} />
                      <YAxis stroke="#666" tick={{ fill: '#888' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Bar 
                        dataKey="sessoes" 
                        name="Sessoes"
                        fill="#32CD32" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  )}

                  {chartType === 'weekly' && (
                    <BarChart data={getWeeklyData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                      <XAxis dataKey="semana" stroke="#666" tick={{ fill: '#888' }} />
                      <YAxis stroke="#666" tick={{ fill: '#888' }} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar 
                        dataKey="sessoes" 
                        name="Sessoes"
                        fill="#32CD32" 
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  )}

                  {chartType === 'types' && (
                    <PieChart>
                      <Pie
                        data={pieChartData.length > 0 ? pieChartData : [{ name: 'Sem dados', value: 1 }]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={120}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {(pieChartData.length > 0 ? pieChartData : [{ name: 'Sem dados', value: 1 }]).map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  )}
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recent Activity */}
        {filteredProgressions.length > 0 && (
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-lg uppercase tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>
                Atividade Recente
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {filteredProgressions
                  .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                  .slice(0, 5)
                  .map((prog, index) => (
                    <div 
                      key={prog.id || index}
                      className="flex items-center justify-between p-4 bg-black/30 rounded-xl border border-white/5"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full bg-[#32CD32]/10 flex items-center justify-center">
                          <Activity className="w-5 h-5 text-[#32CD32]" />
                        </div>
                        <div>
                          <p className="font-medium">{formatDateFull(prog.created_at)}</p>
                          <p className="text-sm text-gray-500">
                            {prog.completed_sessions || 0} sessoes completadas
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1">
                          {[1, 2, 3, 4, 5].map(star => (
                            <Star 
                              key={star} 
                              className={`w-4 h-4 ${star <= (prog.performance_rating || 0) ? 'text-yellow-500 fill-yellow-500' : 'text-gray-600'}`} 
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProgressionPage;
